import { SITE_ORIGIN } from "@/lib/video-seo";
import { absoluteUrl } from "@/lib/absolute-url";

export const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;

export type BlogAuthorRow = {
  name: string;
  slug: string;
  bio?: string | null;
  avatar_url?: string | null;
} | null | undefined;

/**
 * True when the blog_authors row names a real person by a display name.
 *
 * On 1 Oct 2026 the table had 3 rows: "RESTORE Hair" (the brand),
 * "Laura" (a person) and "amandasowadski" (a WordPress user name, not a
 * display name). The brand and a lowercase user name count as the
 * organization. Do not turn a user name into a guessed full name.
 */
export function isPersonAuthor(author: BlogAuthorRow): author is NonNullable<BlogAuthorRow> {
  if (!author?.name) return false;
  const name = author.name.trim();
  if (/restore/i.test(name)) return false;
  return /^[A-Z]/.test(name);
}

/** Gravatar default images ("d=mm") are not a photo of the person. */
function realAvatar(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (/gravatar\.com\/avatar\//.test(url)) return undefined;
  return absoluteUrl(url);
}

export function authorPageUrl(slug: string): string {
  return `${SITE_ORIGIN}/author/${slug}/`;
}

/** The author node for BlogPosting: a Person, or a reference to the organization. */
export function blogAuthorNode(author: BlogAuthorRow): Record<string, unknown> {
  if (!isPersonAuthor(author)) return { "@id": ORGANIZATION_ID };
  return personNode(author);
}

export function personNode(author: NonNullable<BlogAuthorRow>): Record<string, unknown> {
  const image = realAvatar(author.avatar_url);
  const bio = author.bio?.trim();
  return {
    "@type": "Person",
    "@id": `${authorPageUrl(author.slug)}#person`,
    name: author.name.trim(),
    url: authorPageUrl(author.slug),
    ...(image ? { image } : {}),
    ...(bio ? { description: bio } : {}),
    worksFor: { "@id": ORGANIZATION_ID },
  };
}
